import React, { useRef } from 'react';
import { Recommendation } from '../../types'
import { useTrackInscreen } from '../../hooks';
import Tracking from '../../tracking';
import { GetImageComponentByType } from './LegacyImageFormat';
import { IconTorgetShipping24 } from '@fabric-ds/icons/react';
import { Clickable } from '@fabric-ds/react';

//consider moving out to seperate file if used multiple places
const classNames = {
    article: 'cursor-pointer relative overflow-hidden transition-all outline-none sf-ad-outline sf-card-ad rounded-8 p-10 hover:bg-aqua-50 focus:bg-aqua-50',
    imageWrapper: 'aspect-w-1 aspect-h-1 overflow-hidden rounded-8 border border-bluegray-300',
    image: 'w-full h-full object-center object-cover',
    label: 'absolute bottom-0 font-bold text-white py-10 pl-10 pr-16',
    location: 'text-12 py-6',
    locationInner: 'text-gray-500',
    heading: 'h4 mb-0 mb-8 pr-44 break-words',
    headingInner: 'sf-card-ad-link link link--dark hover:no-underline',
    customLabel: 'text-12 py-6 mb-4',
    favoriteHeart: 'absolute top-16 right-16'
};

//troika css leftover, will be removed once fabric solution is found
const labelStyle: React.CSSProperties = {
    background: `
      linear-gradient(
        transparent,
        rgba(0, 0, 0, 0.04935) 13.71%,
        rgba(0, 0, 0, 0.11481) 25.12%,
        rgba(0, 0, 0, 0.1925) 34.61%,
        rgba(0, 0, 0, 0.27852) 42.59%,
        rgba(0, 0, 0, 0.36898) 49.46%,
        rgba(0, 0, 0, 0.46) 55.63%,
        rgba(0, 0, 0, 0.54769) 61.47%,
        rgba(0, 0, 0, 0.62815) 67.41%,
        rgba(0, 0, 0, 0.6975) 73.83%,
        rgba(0, 0, 0, 0.75185) 81.13%,
        rgba(0, 0, 0, 0.78731) 89.73%,
        rgba(0, 0, 0, 0.8))`,
    width: '100%',
};

type AdProps = {
    item: Recommendation;
    imagesCdnUrl: string;
    tracking: Tracking;
    trackAdClick: (adId: string, trackingUrlClick: string[]) => void;
    favoriteHeart: (itemId: string) => React.ReactNode;
}

const StandardAd = (props: AdProps) => {
    const {
        item,
        imagesCdnUrl,
        tracking,
        favoriteHeart,
        trackAdClick
    } = props;

    const domRef = useRef<HTMLDivElement>(null)
    const inscreenTrackingUrls = Tracking.getInscreenUrl(item);
    const trackingUrlClick = Tracking.getClickUrl(item);

    useTrackInscreen({
        adId: item.itemId,
        tracking,
        enabled: true,
        ref: domRef,
        inscreenTrackingUrls: inscreenTrackingUrls,
        pulseContext: item?.tracking?.pulse,
    });

    const handleAdClick = () => {
        trackAdClick(item.itemId, trackingUrlClick);
        tracking.trackClickWithPulse(item.tracking.pulse);
    };

    const getImageUrl = () => {
        if (imagesCdnUrl.includes('images.tori.fi')) {
            return `${item.image.url}?rule=full_hd`;
        }
        return item.image.url;
    };

    const LegacyImageFormat = GetImageComponentByType(
        item.image?.type,
        item.image?.isAbsolute
    );

    const shippingLabel = item.shippingOption?.label;
    const p4pLabel = item.overlay?.p4p?.label;

    return (
        <article ref={domRef} className={classNames.article}>

            {/* image */}
            <div className={classNames.imageWrapper}>
                <LegacyImageFormat
                    alt=""
                    cdnUrl={imagesCdnUrl}
                    height={item.image?.height}
                    sizes="(min-width: 990px) 330px, (min-width: 768px) 33vw, (min-width: 480px) 50vw"
                    url={getImageUrl()}
                    width={item.image?.width}
                    isAbsolute={item.image?.isAbsolute}
                    crop={
                        item.type === 'travel-hotel' || item.type === 'car-sub'
                            ? 'horizontal'
                            : 'vertical'
                    }
                />

                {/* label */}
                {item.label && item.image?.type !== 'LOGO' && (
                    <span>
                        <div className={`${classNames.label}`} style={labelStyle}
                            title="Legg til favoritt">{item.label}
                        </div>
                    </span>
                )}
            </div>

            {/* location */}
            <div className={classNames.location}>
                {item.location && (
                    <span className={classNames.locationInner}>{item.location.combined}</span>
                )}
            </div>

            {/* heading */}
            <h2 className={classNames.heading} id={item.itemId}>
                <Clickable
                    href={`/${item.itemId}`}
                    className={classNames.headingInner}
                    id={item.itemId}
                    onClick={handleAdClick}>
                    {item.heading}
                </Clickable>
            </h2>

            {/* customLabel */}
            <div className={classNames.customLabel}>
                {item.content?.company_name && (<div>{item.content.company_name}</div>)}
                {item.customLabel && <div>{item.customLabel}</div>}
            </div>

            {/* favorite heart */}
            <div className={classNames.favoriteHeart}>{favoriteHeart(item.itemId)}</div>

            {/* shipping label */}
            {
                shippingLabel &&
                (
                    <div className="absolute top-10 left-10 py-4 px-8 inline-flex rounded-tl-8 rounded-br-4 text-12 text-yellow-800 bg-yellow-100">
                        <IconTorgetShipping24 className='w-16 h-16 mr-4' />
                        {shippingLabel}
                    </div>
                )
            }

            {/* ribbon */}
            {
                p4pLabel &&
                (
                    //if shipping label is there, move ribbon down, if not render in corner with border top
                    <p className={`absolute inline-flex bg-aqua-200 text-bluegray-600 text-12 p-4 rounded-4 border-bluegray-300 ${shippingLabel ? 'top-40 left-10' : 'top-28 left-28'}`}>Betalt plassering</p>

                )

            }

            {/* focus ancor point for tabfocus */}
            <div className="absolute inset-0 z-10 pointer-events-none outline-none rounded-8 border-2 border-transparent transition-all sf-card-ad-outline"></div>

        </article >
    );
};

export default StandardAd;